import { Origin } from '../components/manage-installed-apps-page';
import { BundleAppOrigin } from '@wix/ambassador-marketplace/types';
import { AppGroup, ComponentType, IncompleteSetupReason, PaymentCollectionType, RenewalType, } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { getAppsByTag } from '@wix/ambassador-devcenter-appmarket-v1-tag-app/http';
import { HttpClient } from '@wix/http-client';
const httpClient = new HttpClient();
export var MarketPermissions;
(function (MarketPermissions) {
    MarketPermissions["MANAGE_APPS"] = "app-market.manage-app";
    MarketPermissions["UPGRADE_APP"] = "app-market.upgrade-app";
    MarketPermissions["UPDATE_APP"] = "APP_MARKET.UPDATE_APP";
    MarketPermissions["INSTALL_APP"] = "APP_MARKET.INSTALL_APP";
    MarketPermissions["UNINSTALL_APPS"] = "APP_MARKET.UNINSTALL_APPS";
    MarketPermissions["DELETE_APP"] = "APP_MARKET.DELETE_APP";
    MarketPermissions["ADD_AGAIN"] = "APP_MARKET.ADD_AGAIN";
    MarketPermissions["OPEN_APP"] = "APP_MARKET.OPEN_APP";
})(MarketPermissions || (MarketPermissions = {}));
export const isPendingApp = (app) => {
    const installationDetails = app?.installationDetails;
    const { group, incompleteSetupReason } = installationDetails || {};
    return group === AppGroup.PENDING || (group === AppGroup.INSTALLED && incompleteSetupReason === IncompleteSetupReason.PENDING_EDITOR);
};
export const isBusinessApp = (app) => {
    return !isPendingApp(app) && !isRegularApp(app);
};
export const isRegularApp = (app) => {
    return app?.installationDetails?.group === AppGroup.INSTALLED;
};
export const isAppListedAsBundle = (appid, bundleApps = []) => bundleApps?.some((bundleApp) => bundleApp.webSolutionBase.id === appid);
export const isBenefitApp = (appid, benefitApps = []) => benefitApps?.some((app) => app.appId === appid);
export const getAppWidgets = (app) => {
    const comps = app?.components || [];
    return (comps.filter((comp) => comp.compType === ComponentType.WIDGET ||
        comp.compType === ComponentType.WIDGET_OUT_OF_IFRAME) || []);
};
export const isEmbeddedScriptApp = (app) => {
    const comps = app?.components;
    const embeddedComps = comps?.filter((comp) => comp.compType === ComponentType.EMBEDDED_SCRIPT) ||
        [];
    return !!embeddedComps?.length;
};
export const canUsePremiumApp = (app, siteIsPremium) => {
    const premiumInfo = app?.premiumInfo;
    return (siteIsPremium ||
        !(premiumInfo?.requiresWixPremium && premiumInfo?.eligibleForUpgrade));
};
export const openSettingsUrl = (app, embeddedScriptAppsStatus, getAppInstance) => {
    const url = getAppSettingsPageUrl(app);
    const appId = app?.appId;
    const appInstance = getAppInstance(app?.appId);
    let innerUrl = url.includes('?')
        ? `${url}&instance=${appInstance}`
        : `${url}?instance=${appInstance}`;
    if (isEmbeddedScriptApp(app) && embeddedScriptAppsStatus[appId]) {
        const embeddedScriptParams = JSON.stringify(embeddedScriptAppsStatus[appId]);
        innerUrl = `${innerUrl}&embeddedScriptParams=${embeddedScriptParams}`;
    }
    return innerUrl;
};
export const getAppDashboardDataNew = (app) => {
    const dashboardComps = app?.components?.filter((comp) => comp.compType === ComponentType.DASHBOARD ||
        comp.compType === ComponentType.DASHBOARD_PLATFORM ||
        comp.compType === ComponentType.MULTIPLE_DASHBOARDS) || [];
    const compData = dashboardComps?.length
        ? dashboardComps[0]?.compData
        : undefined;
    return compData?.dashboardComponentData
        ? compData.dashboardComponentData
        : compData?.multipleDashboardsComponentData
            ? compData.multipleDashboardsComponentData?.items?.[0]?.dashboardData
            : undefined;
};
export const getBackOfficePageData = (app) => {
    const backOfficePageComp = app?.components?.filter((comp) => comp.compType === ComponentType.BACK_OFFICE_PAGE) || [];
    return backOfficePageComp?.length
        ? backOfficePageComp[0]?.compData?.backOfficePage
        : undefined;
};
export const isDashboardAppNew = (app) => {
    return !!getAppDashboardDataNew(app) || !!getBackOfficePageData(app);
};
export const getAppPages = (app) => {
    const comps = app?.components || [];
    return (comps.filter((comp) => comp.compType === ComponentType.PAGE ||
        comp.compType === ComponentType.PAGE_OUT_OF_IFRAME ||
        comp.compType === ComponentType.UNIFIED_PAGE) || []);
};
export const getBuilderComps = (app) => {
    const comps = app?.components || [];
    return (comps.filter((comp) => comp.compType === ComponentType.STUDIO ||
        comp.compType === ComponentType.STUDIO_WIDGET) || []);
};
export const getEditorPlatformComps = (app) => {
    const comps = app?.components || [];
    return comps.filter((comp) => comp.compType === ComponentType.PLATFORM ||
        comp.compType === ComponentType.EDITOR_ADDON || comp.compType === ComponentType.EDITOR_REACT_COMPONENT) || [];
};
export const getEditorWebComps = (app) => {
    const comps = app?.components || [];
    return (comps.filter((comp) => comp.compType === ComponentType.WEB ||
        comp.compType === ComponentType.CUSTOM_ELEMENT_WIDGET) || []);
};
export const addOnlyOnceApp = (app) => {
    const appWidgets = getAppWidgets(app);
    const appWidgetsData = appWidgets.map((widget) => widget.compData);
    const addOnlyOnceWidgets = appWidgetsData.filter((data) => data.addOnlyOnce ||
        data.widgetComponentData?.addOnlyOnce ||
        data.widgetOutOfIframeData?.widgetData?.addOnlyOnce);
    return addOnlyOnceWidgets?.length || getAppPages(app)?.length;
};
export const shouldShowAddAgain = (app, origin) => {
    const isAddOnlyOnceApp = addOnlyOnceApp(app);
    return (!isAddOnlyOnceApp &&
        origin === Origin.EDITOR &&
        appHasEditor(app) &&
        !isBlocksApp(app));
};
export const isPermittedToDelete = (app, permissions) => {
    return (!!appHasEditor(app) ||
        permissions?.some((p) => p === MarketPermissions.UNINSTALL_APPS));
};
function doesAppHaveVisualBlocksComponent(app) {
    const appComponents = app.components ?? [];
    return appComponents.some((component) => component.compType === ComponentType.STUDIO_WIDGET);
}
export function newAppHasEditor(app) {
    return (getAppPages(app)?.length ||
        getAppWidgets(app)?.length ||
        doesAppHaveVisualBlocksComponent(app) ||
        getEditorPlatformComps(app)?.length ||
        getEditorWebComps(app)?.length);
}
export const appHasEditor = (app) => getAppPages(app)?.length ||
    getAppWidgets(app)?.length ||
    getBuilderComps(app)?.length ||
    getEditorPlatformComps(app)?.length ||
    getEditorWebComps(app)?.length;
export const isBlocksApp = (app) => getBuilderComps(app)?.length > 0;
export const getAppSettingsPageUrl = (app) => getAppDashboardDataNew(app)?.settingsPageUrl;
export const isWixApp = (companyId) => companyId === 'ad147a73-a40e-4e50-8344-2ad33592790d' ||
    companyId === 'a60ac714-5c5a-4c38-a285-573752f04876';
export const wixChatBotLink = 'https://www.wix.com/support/conversations/category/contact';
export const isBundleApp = (appId, bundleApps, benefitApps, experiments) => {
    if (experiments?.enabled('specs.app-market.AppBenefits')) {
        return !!benefitApps?.find((app) => app.appId === appId) || !!bundleApps?.find((app) => app.webSolutionBase.id === appId);
    }
    else {
        return !!bundleApps?.find((app) => app.webSolutionBase.id === appId);
    }
};
export const isBenefitAppInstalled = ({ appId, benefitApps }) => {
    return !!(benefitApps?.find((app) => app.appId === appId)?.installationDetails?.group === 'INSTALLED');
};
export const isBundleAppInstalled = ({ appId, bundleApps }) => {
    return !!bundleApps?.find((app) => app.webSolutionBase.id === appId)?.upgradeSuggestion;
};
export const isBundleAppAndInstalled = ({ appId, bundleApps, benefitApps, experiments }) => {
    const isBundleInstalled = isBundleAppInstalled({ appId, bundleApps });
    const isBenefitInstalled = isBenefitAppInstalled({ appId, benefitApps });
    return isBundleInstalled || isBenefitInstalled;
};
export const isBundleAppAndNotInstalled = ({ appId, bundleApps, benefitApps, experiments }) => {
    if (!isBundleApp(appId, bundleApps, benefitApps, experiments)) {
        return false;
    }
    const isBundleInstalled = isBundleAppInstalled({ appId, bundleApps });
    const isBenefitInstalled = isBenefitAppInstalled({ appId, benefitApps });
    return !isBundleInstalled && !isBenefitInstalled;
};
export const isBundleAppFromVoucher = (appId, bundleApps) => {
    const bundleApp = bundleApps?.find((app) => app.webSolutionBase.id === appId);
    if (bundleApp) {
        return bundleApp?.appOrigin === BundleAppOrigin.VOUCHER;
    }
    return false;
};
export const isNewVersionAvailable = (app) => !!app?.installationDetails?.shouldUpdateVersion;
export const isPremiumFromBundle = (app) => {
    const isPremiumFromBundle = app?.premiumInfo?.isUpgraded &&
        app?.premiumInfo?.paymentCollectionType ===
            PaymentCollectionType.NON_PAYABLE &&
        app?.premiumInfo?.renewalType === RenewalType.UNKNOWN;
    return isPremiumFromBundle;
};
export const isSetupIncomplete = (app) => (app?.installationDetails?.incompleteSetupReason ===
    IncompleteSetupReason.PENDING_CONFIGURATION ||
    app?.installationDetails?.incompleteSetupReason ===
        IncompleteSetupReason.PENDING_EDITOR ||
    app?.installationDetails?.incompleteSetupReason ===
        IncompleteSetupReason.PENDING_CONSENT) &&
    !isPendingApp(app);
export const isPendingConfiguration = (app) => app?.installationDetails?.incompleteSetupReason ===
    IncompleteSetupReason.PENDING_CONFIGURATION && !isPendingApp(app);
export const isHotelsAndNotPending = (app, experiments) => {
    const appId = app?.appId;
    const HOTEL_RUNNER_APP_ID = '826670ba-3a6b-4157-ac72-7c4fca9ce220';
    const HOTEL_RUNNER_STAGING_APP_ID = 'ee4f5349-bf6f-46a8-aaf6-8cba4f9726ff';
    const isHotels = appId === HOTEL_RUNNER_APP_ID || appId === HOTEL_RUNNER_STAGING_APP_ID;
    if (isHotels &&
        !isPendingApp(app) &&
        !isSetupIncomplete(app) &&
        experiments.enabled('specs.marketplace.hotelsBetaBadge')) {
        return true;
    }
};
export const isExternalDashboard = (app) => {
    const dashboardData = getAppDashboardDataNew(app);
    const isEmbedded = dashboardData && dashboardData.embedded;
    const externalDashboardUrl = dashboardData && dashboardData.url;
    return !!(!isEmbedded && externalDashboardUrl);
};
export const isMLApp = (appId) => appId === '14d84998-ae09-1abf-c6fc-3f3cace5bf19';
export const isSlotsApp = async (appId) => {
    const effectiveAppsTagId = '2dd9f394-9866-46a9-baf4-653af42e375e';
    const getAppsByTagResponse = await httpClient.request(getAppsByTag({
        tagId: effectiveAppsTagId,
    }));
    const isSlotsApps = getAppsByTagResponse.data.tagApps?.some((tagApp) => tagApp.appId === appId);
    return isSlotsApps;
};
//# sourceMappingURL=appUtils.js.map